import React from "react";
import { motion } from "framer-motion";
import { useReducedMotion } from "../../hooks/useReducedMotion";
import {
  GreetingContainer,
  TimeGreeting,
  ContextMessage,
  GreetingIcon,
} from "./styles";
import {
  getPersonalizedGreeting,
  getContextualMessage,
} from "../../utils/greetings";
import WavingHandIcon from "@mui/icons-material/WavingHand";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CelebrationIcon from "@mui/icons-material/Celebration";
import InfoIcon from "@mui/icons-material/Info";

export interface KLPersonalizedGreetingProps {
  /**
   * Business name for personalization
   */
  businessName?: string;
  /**
   * Owner/user name for personalization
   */
  ownerName?: string;
  /**
   * Whether business has services defined
   */
  hasServices: boolean;
  /**
   * Whether business has customers
   */
  hasCustomers: boolean;
  /**
   * Whether there are upcoming appointments
   */
  hasUpcomingSlots: boolean;
  /**
   * Show compact version
   */
  compact?: boolean;
}

const KLPersonalizedGreeting: React.FC<KLPersonalizedGreetingProps> = ({
  businessName,
  ownerName,
  hasServices,
  hasCustomers,
  hasUpcomingSlots,
  compact = false,
}) => {
  const reducedMotion = useReducedMotion();

  const greeting = getPersonalizedGreeting(ownerName);
  const contextMessage = getContextualMessage({
    hasServices,
    hasCustomers,
    hasUpcomingSlots,
    businessName,
  });

  // Choose icon based on business state
  const getIcon = () => {
    if (!hasServices) {
      return <WavingHandIcon />;
    }
    if (hasServices && hasCustomers && hasUpcomingSlots) {
      return <CelebrationIcon />;
    }
    if (hasServices && hasCustomers) {
      return <TrendingUpIcon />;
    }
    return <InfoIcon />;
  };

  return (
    <GreetingContainer
      $compact={compact}
      as={motion.div}
      role="status"
      aria-live="polite"
      aria-atomic="true"
      initial={reducedMotion ? {} : { opacity: 0, y: -10 }}
      animate={reducedMotion ? {} : { opacity: 1, y: 0 }}
      transition={
        reducedMotion
          ? { duration: 0 }
          : { duration: 0.4, ease: "easeOut", delay: 0.1 }
      }
    >
      <GreetingIcon
        as={motion.div}
        initial={reducedMotion ? {} : { scale: 0, rotate: -180 }}
        animate={reducedMotion ? {} : { scale: 1, rotate: 0 }}
        transition={
          reducedMotion
            ? { duration: 0 }
            : {
                type: "spring",
                stiffness: 200,
                damping: 15,
                delay: 0.3,
              }
        }
      >
        {getIcon()}
      </GreetingIcon>
      <div>
        <TimeGreeting>{greeting}</TimeGreeting>
        {!compact && <ContextMessage>{contextMessage}</ContextMessage>}
      </div>
    </GreetingContainer>
  );
};

export default KLPersonalizedGreeting;
